import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, Search, Eye, MousePointer, Zap, Image, Calculator, PenTool, Clock, Coins, BarChart3 } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { type Question, QUESTION_TYPE_REGISTRY } from '../../../../content';
import { ADMIN_MODULE_META } from '../../shared/moduleMetadata';
import { containerVariants, itemVariants } from '../../shared/animationVariants';
interface TypesTabProps {
  questions: Question[];
  onSelectType: (type: string) => void;
}
const getTypeIcon = (type: string) => {
  if (['multiple_choice', 'cijferend_rekenen', 'cijferend_delen', 'number_sequence', 'ratio_table', 'multiplication_grid', 'estimation_slider', 'thermometer_slider'].includes(type)) return Calculator;
  if (['fill_in', 'drag_drop_fill', 'matching', 'ordering', 'text_highlight'].includes(type)) return PenTool;
  if (['clock', 'schedule_reading'].includes(type)) return Clock;
  if (['money'].includes(type)) return Coins;
  if (['bar_chart', 'line_chart', 'pie_chart', 'table_reading'].includes(type)) return BarChart3;
  return Layers;
};
const TYPE_COLORS = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500'];
// Color map for module badges (same pattern as VisualsTab)
const MODULE_BADGE_COLORS: Record<string, {
  dot: string;
}> = {};
Object.entries(ADMIN_MODULE_META).forEach(([code, meta]) => {
  MODULE_BADGE_COLORS[code] = {
    dot: meta.color
  };
});
function ModuleBadge({
  code
}: {
  code: string;
}) {
  const colors = MODULE_BADGE_COLORS[code] || {
    dot: 'bg-gray-500'
  };
  return <span className="text-[10px] font-bold px-2 py-1 rounded-md border bg-gray-50 border-gray-200 text-gray-700 inline-flex items-center gap-1" title={code} data-id="element-4866">
      <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} data-id="element-4867" />
      {code}
    </span>;
}
export function TypesTab({
  questions,
  onSelectType
}: TypesTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const totalQuestions = questions.length;
  // Count questions per type AND track modules per type
  const {
    typeCounts,
    typeModules
  } = useMemo(() => {
    const counts: Record<string, number> = {};
    const modules: Record<string, Set<string>> = {};
    questions.forEach(q => {
      counts[q.questionType] = (counts[q.questionType] || 0) + 1;
      if (!modules[q.questionType]) modules[q.questionType] = new Set();
      const moduleCode = q.familyId?.split('-')[0] || 'UNK';
      modules[q.questionType].add(moduleCode);
    });
    return {
      typeCounts: counts,
      typeModules: modules
    };
  }, [questions]);
  // Build type list with metadata
  const typesList = useMemo(() => {
    return Object.entries(QUESTION_TYPE_REGISTRY).map(([type, config]) => ({
      type,
      ...config,
      count: typeCounts[type] || 0,
      modules: typeModules[type] || new Set<string>(),
      usagePct: totalQuestions > 0 ? Math.round((typeCounts[type] || 0) / totalQuestions * 100) : 0,
      exampleQuestion: questions.find(q => q.questionType === type)?.question || 'Geen voorbeeld beschikbaar'
    })).sort((a, b) => b.count - a.count);
  }, [typeCounts, typeModules, totalQuestions, questions]);
  // Filter by search
  const filteredTypes = useMemo(() => {
    if (!searchQuery.trim()) return typesList;
    const query = searchQuery.toLowerCase();
    return typesList.filter(t => t.label.toLowerCase().includes(query) || t.type.toLowerCase().includes(query));
  }, [typesList, searchQuery]);
  // Stats
  const activeTypes = typesList.filter(t => t.count > 0).length;
  const totalTypes = typesList.length;
  return <div className="space-y-6" data-id="element-4868">
      {/* Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-4869">
        <motion.div variants={itemVariants} data-id="element-4870">
          <StatCard label="Geregistreerde Typen" value={totalTypes} icon={<Layers className="w-5 h-5" data-id="element-4872" />} color="blue" data-id="element-4871" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4873">
          <StatCard label="Actief in Gebruik" value={activeTypes} icon={<Zap className="w-5 h-5" data-id="element-4875" />} color="emerald" data-id="element-4874" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4876">
          <StatCard label="Totaal Vragen" value={totalQuestions.toLocaleString('nl-NL')} icon={<Calculator className="w-5 h-5" data-id="element-4878" />} color="purple" data-id="element-4877" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4879">
          <StatCard label="Interactieve Typen" value={typesList.filter(t => t.isInteractive && t.count > 0).length} icon={<MousePointer className="w-5 h-5" data-id="element-4881" />} color="amber" data-id="element-4880" />
        </motion.div>
      </motion.div>

      {/* Distribution Bar + Search */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4882">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4883">
          <div data-id="element-4884">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-4885">
              <Layers className="w-5 h-5 text-blue-500" data-id="element-4886" />
              Vraagtypen Overzicht
            </h3>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-4887">
              {activeTypes} van {totalTypes} typen actief in gebruik
            </p>
          </div>
          <div className="w-full sm:w-64" data-id="element-4888">
            <Input type="text" placeholder="Zoek vraagtype..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-4890" />} variant="admin" size="sm" data-id="element-4889" />
          </div>
        </div>

        {/* Distribution visualization */}
        <div className="px-4 sm:px-5 pt-4 pb-2" data-id="element-4891">
          <div className="flex items-center justify-between mb-2" data-id="element-4892">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4893">
              Verdeling ({totalQuestions.toLocaleString('nl-NL')} vragen)
            </span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4894">
            {typesList.map((t, i) => {
            if (t.count === 0) return null;
            return <motion.div key={t.type} initial={{
              width: 0
            }} animate={{
              width: `${t.usagePct}%`
            }} transition={{
              duration: 0.6,
              delay: i * 0.02
            }} className={`h-full ${TYPE_COLORS[i % TYPE_COLORS.length]}`} title={`${t.label}: ${t.count} (${t.usagePct}%)`} data-id="element-4895" />;
          })}
          </div>
        </div>

        {/* Type Cards Grid */}
        <div className="p-4 sm:p-5" data-id="element-4896">
          {filteredTypes.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-4897">
              Geen vraagtypen gevonden voor "{searchQuery}"
            </div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4898">
              {filteredTypes.map((t, index) => {
            const Icon = getTypeIcon(t.type);
            return <motion.div key={t.type} variants={itemVariants} data-id="element-4899">
                    <Card className="p-5 flex flex-col h-full border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group" data-id="element-4900">
                      <div className="flex justify-between items-start mb-4" data-id="element-4901">
                        <div className="flex items-center gap-3" data-id="element-4902">
                          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 group-hover:scale-105 transition-transform" data-id="element-4903">
                            <Icon className="w-5 h-5" data-id="element-4904" />
                          </div>
                          <div className="min-w-0" data-id="element-4905">
                            <h3 className="font-bold text-gray-900 text-sm truncate" data-id="element-4906">
                              {t.label}
                            </h3>
                            <code className="text-[10px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-4907">
                              {t.type}
                            </code>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0" data-id="element-4908">
                          <div className={`text-xl font-black ${t.count > 0 ? 'text-blue-600' : 'text-gray-300'}`} data-id="element-4909">
                            {t.count}
                          </div>
                          <div className="text-[10px] text-gray-400 font-medium" data-id="element-4910">
                            {t.usagePct}%
                          </div>
                        </div>
                      </div>

                      {/* Example question */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4 flex-1 border border-gray-100" data-id="element-4911">
                        <p className="text-[10px] text-gray-500 font-semibold mb-1 uppercase tracking-wider" data-id="element-4912">
                          Voorbeeld
                        </p>
                        <p className="text-xs text-gray-700 line-clamp-2 italic" data-id="element-4913">
                          "{t.exampleQuestion}"
                        </p>
                      </div>

                      {/* Badges + Module Tags + Action */}
                      <div className="mt-auto space-y-3" data-id="element-4914">
                        <div className="flex flex-wrap gap-1.5" data-id="element-4915">
                          {t.isInteractive ? <Badge variant="info" size="sm" data-id="element-4916">
                              <MousePointer className="w-3 h-3 mr-1" data-id="element-4917" />
                              Interactief
                            </Badge> : <Badge variant="neutral" size="sm" data-id="element-4918">
                              Statisch
                            </Badge>}
                          {t.isCitoEligible && <Badge variant="success" size="sm" data-id="element-4919">
                              <Zap className="w-3 h-3 mr-1" data-id="element-4920" />
                              CITO
                            </Badge>}
                          {t.hasBuiltInVisuals && <Badge variant="warning" size="sm" data-id="element-4921">
                              <Image className="w-3 h-3 mr-1" data-id="element-4922" />
                              Visuals
                            </Badge>}
                        </div>

                        {/* Module tags */}
                        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100" data-id="element-4923">
                          {t.modules.size > 0 ? Array.from(t.modules).sort().slice(0, 5).map(m => <ModuleBadge key={m} code={m} data-id="element-4924" />) : <span className="text-xs text-gray-400 italic" data-id="element-4925">
                              Niet in gebruik
                            </span>}
                          {t.modules.size > 5 && <span className="text-[10px] text-gray-400 font-medium px-1.5 py-0.5" data-id="element-4926">
                              +{t.modules.size - 5}
                            </span>}
                        </div>

                        <Button variant="secondary" size="sm" className="w-full justify-center" onClick={() => onSelectType(t.type)} disabled={t.count === 0} data-id="element-4927">
                          <Eye className="w-4 h-4 mr-1.5" data-id="element-4928" />
                          Bekijk {t.count} vragen
                        </Button>
                      </div>
                    </Card>
                  </motion.div>;
          })}
            </motion.div>}
        </div>
      </div>
    </div>;
}